import { useQuery, UseQueryOptions } from 'react-query';
import { parseISO, setMilliseconds } from 'date-fns';

import client from 'client';

export type ScheduledMeetingTimesResponse = {
    /**
     * ISO string dates e.g. 2021-12-10T17:00:00
     */
    times: string[];
    /**
     * Duration of a scheduled meeting in milliseconds
     */
    duration: number;
};

const fetchScheduledMeetingTimes = async (eventId: number, userIds: number[]) => {
    const response = await client().get<ScheduledMeetingTimesResponse>(
        `/api/v2/meetings/events/${eventId}/timesAvailable?guestIds=${userIds.join(',')}`
    );
    return response.data;
};

type ScheduledMeetingTimes = {
    times: Date[];
    duration: number;
};

/**
 *
 * @param eventId Ring event for context
 * @param userIds List of other user IDs to use as the context along with the current user
 * @param options UseQueryOptions object
 * @returns List of available meeting dates and duration based on the user's preferences and current availability
 */
export const useScheduledMeetingTimes = (
    eventId: number,
    userIds: number[],
    options?: UseQueryOptions<ScheduledMeetingTimesResponse, Error, ScheduledMeetingTimes>
) => {
    return useQuery<ScheduledMeetingTimesResponse, Error, ScheduledMeetingTimes>(
        ['meetings', userIds.join(',')],
        () => fetchScheduledMeetingTimes(eventId, userIds),
        {
            select: data => ({ ...data, times: parseISOHandle(data.times) }),
            staleTime: 0,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            ...options,
        }
    );
};

const parseISOHandle = (times: string[] = []) =>
    times.map(time => setMilliseconds(parseISO(time), 0));

export default useScheduledMeetingTimes;
